/**
 * Post-dated cheques: where one stands today.
 *
 * Whether a cheque is due is worked out from its date every time it is read,
 * never stored. A stored "due today" is only ever true on the day it was
 * written: the reference implementation had one, and a cheque marked due on the
 * 29th kept being added into every later day's presentation total.
 */
import type { MemberId } from './tenant.js';

export enum ChequeStatus {
    /** In hand, waiting for its date. */
    Pending = 'Pending',
    /** The bank paid it. */
    Cleared = 'Cleared',
    /** Deliberately not being presented. */
    Hold = 'Hold',
    /** Returned unpaid. */
    Bounced = 'Bounced',
}

/** The statuses a person may actually choose, in the order they happen. */
export const CHEQUE_STATUS_CHOICES: { value: ChequeStatus; label: string }[] = [
    { value: ChequeStatus.Pending, label: 'Pending — waiting for its date' },
    { value: ChequeStatus.Hold, label: 'On hold (customer request)' },
    { value: ChequeStatus.Cleared, label: 'Cleared' },
    { value: ChequeStatus.Bounced, label: 'Bounced / returned' },
];

export interface Cheque {
    id: string;
    customerId: string;
    customerName: string;
    chequeNumber: string;
    bankName: string;
    /** The date written on the cheque. */
    chequeDate: Date;
    amount: number;
    status: ChequeStatus;
    receivedDate: Date;
    clearedDate?: Date;
    remarks?: string;
    ownerMemberId?: MemberId;
    addedByMemberId?: MemberId;
}

/**
 * Where a cheque stands today.
 *
 *   cleared / bounced / hold  — somebody decided; the date no longer matters
 *   due                       — in hand, dated today: present it
 *   overdue                   — in hand, its date has gone: bank it or say why
 *   upcoming                  — in hand, still waiting for its date
 *
 * "overdue" had nowhere to go before this, so a past-dated cheque fell in with
 * the ones still waiting and nothing ever prompted anyone to bank it.
 */
export type ChequeState = 'due' | 'overdue' | 'upcoming' | 'hold' | 'cleared' | 'bounced';

export function chequeState(
    cheque: Pick<Cheque, 'status' | 'chequeDate'>,
    today: Date = new Date(),
): ChequeState {
    if (cheque.status === ChequeStatus.Cleared) return 'cleared';
    if (cheque.status === ChequeStatus.Bounced) return 'bounced';
    if (cheque.status === ChequeStatus.Hold) return 'hold';

    const on = new Date(cheque.chequeDate);
    if (isNaN(on.getTime())) return 'upcoming';
    on.setHours(0, 0, 0, 0);
    const t = new Date(today);
    t.setHours(0, 0, 0, 0);

    if (on.getTime() === t.getTime()) return 'due';
    return on.getTime() < t.getTime() ? 'overdue' : 'upcoming';
}

/** A cheque in these states is still money in hand: the bank has not acted. */
export const CHEQUE_ACTIVE: readonly ChequeState[] = ['due', 'overdue', 'upcoming', 'hold'];

export const isChequeActive = (state: ChequeState): boolean => CHEQUE_ACTIVE.includes(state);

/* ------------------------------ the six words ----------------------------- */

/**
 * The six places a cheque can be, in the words the whole product uses.
 *
 * In the reference implementation the register, its tiles, the customer dialog
 * and the morning summary each had their own spelling — "Today's Due", "Date
 * passed, not banked", "Pending", "Overdue", "Upcoming PDCs" — for the same six
 * states. One table, one label each, and one line saying what it means, so a
 * count and a badge cannot describe the same cheque differently.
 */
export interface ChequeStateMeaning {
    label: string;
    /** What the state means, for a tile's second line or a badge's title. */
    hint: string;
    /** Which way the figure leans, for the UI to colour. Never a colour here. */
    tone: 'dang' | 'warn' | 'pos' | 'neutral' | 'brand';
}

export const CHEQUE_STATES: Record<ChequeState, ChequeStateMeaning> = {
    overdue: {
        label: 'Date passed',
        hint: 'In hand and its date has gone — bank it, or say why not',
        tone: 'dang',
    },
    due: {
        label: 'Due today',
        hint: 'In hand and dated today — present it at the bank',
        tone: 'warn',
    },
    bounced: {
        label: 'Bounced',
        hint: 'Returned unpaid by the bank — needs a call',
        tone: 'dang',
    },
    hold: {
        label: 'On hold',
        hint: 'Deliberately not being presented, usually at the customer’s request',
        tone: 'warn',
    },
    upcoming: {
        label: 'Upcoming',
        hint: 'In hand, waiting for its date',
        tone: 'brand',
    },
    cleared: {
        label: 'Cleared',
        hint: 'The bank paid it',
        tone: 'pos',
    },
};

/** The states in the order a person needs them: what needs doing first. */
export const CHEQUE_STATE_ORDER: readonly ChequeState[] =
    ['overdue', 'due', 'bounced', 'hold', 'upcoming', 'cleared'];

export const isChequeState = (value: unknown): value is ChequeState =>
    CHEQUE_STATE_ORDER.includes(value as ChequeState);

/* ----------------------------- what you may do ---------------------------- */

/**
 * The actions a cheque in a given state actually has.
 *
 * Stated as data rather than as branches in a component, because the same
 * question is asked by the row, by the bulk bar and by the test that proves a
 * cleared cheque carries no "Clear". A cheque that has been paid is not
 * collection work, and offering the collection actions on it is how somebody
 * marks a cleared cheque cleared again and wonders why the total moved.
 */
export interface ChequeAction {
    /** The button's words. */
    label: string;
    /** What it means, for the title attribute. Never a bare verb. */
    title: string;
    /** The status the cheque moves to. */
    to: ChequeStatus;
    tone: 'pos' | 'warn' | 'dang' | 'quiet';
}

const CLEAR: ChequeAction = {
    label: 'Clear', to: ChequeStatus.Cleared, tone: 'pos',
    title: 'Mark cleared — the bank paid it',
};
const HOLD: ChequeAction = {
    label: 'Hold', to: ChequeStatus.Hold, tone: 'warn',
    title: 'Put on hold — do not present it for now',
};
const BOUNCE: ChequeAction = {
    label: 'Bounce', to: ChequeStatus.Bounced, tone: 'dang',
    title: 'Mark bounced — returned unpaid by the bank',
};

export function chequeActions(state: ChequeState): readonly ChequeAction[] {
    switch (state) {
        // In hand, whatever its date: it can be banked, held, or come back unpaid.
        case 'due':
        case 'overdue':
        case 'upcoming':
            return [CLEAR, HOLD, BOUNCE];

        case 'hold':
            return [CLEAR, {
                label: 'Release', to: ChequeStatus.Pending, tone: 'quiet',
                title: 'Release the hold — back in hand, present it when it is due',
            }, BOUNCE];

        case 'bounced':
            return [
                { ...CLEAR, title: 'Mark cleared — re-presented and paid' },
                {
                    label: 'Back in hand', to: ChequeStatus.Pending, tone: 'quiet',
                    title: 'Not bounced after all — back in hand, waiting for its date',
                },
            ];

        // Quiet, and alone. A cleared cheque is history; the only thing left to
        // do with it is say it was not cleared after all.
        case 'cleared':
            return [{
                label: 'Undo', to: ChequeStatus.Pending, tone: 'quiet',
                title: 'Not cleared after all — back in hand, waiting for its date',
            }];
    }
}

/** Whether a cheque in this state may be moved to that status at all. */
export const chequeTransitionAllowed = (from: ChequeState, to: ChequeStatus): boolean =>
    chequeActions(from).some(a => a.to === to);

/**
 * The register's order, for anything that sorts cheques outside the database.
 *
 * What is in hand first, oldest date first, so the cheque that should have been
 * banked last week is at the top; then the exceptions; then cleared, newest
 * first, because a cleared cheque is history. Sorting the whole register by
 * date put August's cleared cheques above this morning's work.
 *
 * `cheque_page()` orders identically in SQL. This exists for the test double
 * and for any small list the browser assembles itself.
 */
const SORT_GROUP: Record<ChequeState, number> = {
    overdue: 0, due: 0, upcoming: 0, bounced: 1, hold: 2, cleared: 3,
};

export function sortCheques<T extends { chequeDate: Date | string; state: ChequeState }>(rows: T[]): T[] {
    const time = (value: Date | string): number => {
        const d = value instanceof Date ? value : new Date(value);
        return isNaN(d.getTime()) ? 0 : d.getTime();
    };
    return [...rows].sort((a, b) => {
        const group = SORT_GROUP[a.state] - SORT_GROUP[b.state];
        if (group !== 0) return group;
        const byDate = time(a.chequeDate) - time(b.chequeDate);
        return a.state === 'cleared' ? -byDate : byDate;
    });
}
